/** Lo que se escribe en una tarjeta sin dato, en el idioma del sitio. */
const CARD_TEXTS = {
    es: { message: 'Envíanos un mensaje', phone: 'Agrega tu teléfono', email: 'Agrega tu correo', address: 'Agrega tu dirección', web: 'Agrega tu sitio o red', social: 'Agrega tu red social' },
    en: { message: 'Send us a message', phone: 'Add your phone', email: 'Add your email', address: 'Add your address', web: 'Add your website or social', social: 'Add your social profile' },
};
/** Qué tarjeta es cada una, por su icono, y qué datos le sirven, por orden. */
const SLOT_BY_ICON = {
    phone: 'phone',
    mail: 'email',
    send: 'email',
    message: 'chat',
    whatsapp: 'chat',
    mapPin: 'address',
    map: 'address',
    building: 'address',
    globe: 'web',
    link: 'web',
    instagram: 'instagram',
    facebook: 'facebook',
    linkedin: 'linkedin',
    youtube: 'youtube',
};
const KINDS_BY_SLOT = {
    phone: ['phone', 'whatsapp'],
    email: ['email'],
    chat: ['whatsapp', 'instagram', 'facebook'],
    address: ['address'],
    web: ['website', 'instagram', 'facebook'],
    instagram: ['instagram'],
    facebook: ['facebook'],
    linkedin: ['linkedin'],
    youtube: ['youtube'],
};
const SOCIAL_BASE = {
    instagram: 'https://instagram.com/',
    facebook: 'https://facebook.com/',
    linkedin: 'https://linkedin.com/in/',
    tiktok: 'https://tiktok.com/@',
    x: 'https://x.com/',
    youtube: 'https://youtube.com/@',
};
/** A dónde lleva un dato al pulsarlo. */
export function contactHref(kind, value) {
    const text = value.trim();
    if (kind === 'email')
        return `mailto:${text}`;
    if (kind === 'phone')
        return `tel:${text.replace(/[^\d+]/g, '')}`;
    if (kind === 'whatsapp')
        return `https://wa.me/${text.replace(/[^\d]/g, '')}`;
    if (kind === 'address')
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;
    if (/^https?:\/\//i.test(text))
        return text;
    if (kind === 'website')
        return `https://${text.replace(/^\/+/, '')}`;
    const base = SOCIAL_BASE[kind];
    return base ? base + text.replace(/^@/, '') : '#';
}
/**
 * El enlace de una tarjeta de contacto a partir de su ranura y de lo que se
 * escribió en ella: «+52 55 1234» en la del teléfono da `tel:+52551234`. Lo
 * usa el editor al completar una tarjeta a mano, para que no se quede en `#`.
 * `null` si la ranura no sirve (la de mensaje lleva al formulario) o no hay dato.
 */
export function contactSlotHref(slot, value) {
    if (typeof slot !== 'string' || !(slot in KINDS_BY_SLOT) || slot === 'chat')
        return null;
    const text = value.trim();
    if (!text)
        return null;
    const href = contactHref(KINDS_BY_SLOT[slot][0], text);
    return href === '#' ? null : href;
}
/** Lo que se lee en la tarjeta. */
function contactLabel(kind, value) {
    const text = value.trim();
    if (SOCIAL_BASE[kind] && !/^https?:\/\//i.test(text))
        return `@${text.replace(/^@/, '')}`;
    return text.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
}
const isAction = (node) => node.type === 'link' || node.type === 'button';
/** El hueco de contacto de un texto, por su `aiSlot` (`contact.phone` → `phone`). */
const SLOT_BY_AI_SLOT = { 'contact.phone': 'phone', 'contact.email': 'email', 'contact.address': 'address' };
function aiContactSlot(node) {
    const slot = typeof node.aiSlot === 'string' ? node.aiSlot.split(':')[0] : '';
    return SLOT_BY_AI_SLOT[slot] ?? null;
}
const TEXT_TYPES = new Set(['text', 'link', 'button']);
/** El enlace de una tarjeta y su ranura, si el nodo es una tarjeta de contacto. */
function cardParts(node) {
    /* Un enlace o botón; o, en una fila de icono y texto (el teléfono junto a
       su icono, sin enlace), ese texto: no se llenaba y salía el de muestra
       («info@hello.com») como texto suelto (2026-10-30). */
    const texts = node.children.filter((child) => child.type === 'text');
    const action = node.children.find(isAction) ?? (texts.length === 1 && node.children.some((child) => child.type === 'icon') ? texts[0] : undefined);
    if (!action)
        return null;
    const marked = typeof action.contactSlot === 'string' && action.contactSlot in KINDS_BY_SLOT ? action.contactSlot : null;
    if (marked)
        return { action, slot: marked };
    const icon = node.children.find((child) => child.type === 'icon' && typeof child.iconName === 'string' && child.iconName in SLOT_BY_ICON);
    return icon ? { action, slot: SLOT_BY_ICON[icon.iconName] } : null;
}
function fill(action, slot, entries, options = {}) {
    action.contactSlot = slot;
    const entry = KINDS_BY_SLOT[slot]
        .map((kind) => entries.find((item) => item.kind === kind && item.value.trim()))
        .find(Boolean);
    const texts = CARD_TEXTS[options.language === 'es' ? 'es' : 'en'];
    /* También un texto: se dibuja como enlace (llamar, escribir, el mapa). Él lo
       pidió: los datos de contacto siempre con su enlace (2026-10-30). */
    const linkable = isAction(action) || action.type === 'text';
    const link = (href) => ({ ...(action.interaction ?? {}), action: href.startsWith('#') ? 'anchor' : 'link', href });
    if (entry) {
        action.content = contactLabel(entry.kind, entry.value);
        if (linkable)
            action.interaction = link(contactHref(entry.kind, entry.value));
        delete action.contactPending;
        return;
    }
    if (slot === 'chat') {
        /* Escribir no necesita dato: al formulario. */
        action.content = texts.message;
        const current = action.interaction?.href;
        const href = options.messageHref ?? (typeof current === 'string' && current && current !== '#' ? current : '#contacto');
        action.interaction = link(href);
        delete action.contactPending;
        return;
    }
    const group = slot === 'phone' || slot === 'email' || slot === 'address' || slot === 'web' ? slot : 'social';
    action.content = texts[group];
    /* Sin dato, un texto no lleva enlace (no tiene a dónde ir); un botón, el suyo vacío. */
    if (isAction(action))
        action.interaction = link('#');
    else
        delete action.interaction;
    action.contactPending = true;
}
/**
 * Marca y rellena los textos con hueco de contacto (worker y asistente), en
 * cualquier sección. Va ANTES de escribir los textos de la IA: así el borrado
 * de lo no escrito (`stripUnwritten`) los reconoce por su marca y los deja.
 */
export function markContactSlots(nodes, entries, options = {}) {
    const visit = (node) => {
        const slot = TEXT_TYPES.has(node.type) ? aiContactSlot(node) : null;
        if (slot)
            fill(node, slot, entries, options);
        labelContactSiblings(node, options);
        node.children.forEach(visit);
    };
    nodes.forEach(visit);
}
const SLOT_LABELS = {
    es: { phone: 'Teléfono', email: 'Correo', address: 'Dirección' },
    en: { phone: 'Phone', email: 'Email', address: 'Address' },
};
/**
 * El antetítulo que va junto a un dato de contacto es SU etiqueta («Call us»
 * sobre el teléfono), no el de la sección: con el reparto de textos los tres
 * salían «Contacto». Toma el nombre del dato en el idioma del sitio y deja de
 * ser un hueco de la IA.
 */
function labelContactSiblings(parent, options) {
    /* Sólo en un grupo de etiqueta y dato: donde hay título o subtítulo, el
       antetítulo es el de la sección (el «Say hello» junto al correo grande). */
    const isHeader = parent.type === 'section' || parent.children.some((child) => {
        const base = typeof child.aiSlot === 'string' ? child.aiSlot.split(':')[0].replace(/\.\d+$/, '') : '';
        return /^(headline|subheadline|slot-title|slot-subtitle)$/.test(base);
    });
    if (isHeader)
        return;
    const contact = parent.children.map((child) => (TEXT_TYPES.has(child.type) ? aiContactSlot(child) : null)).find(Boolean);
    if (!contact)
        return;
    const label = SLOT_LABELS[options.language === 'es' ? 'es' : 'en'][contact];
    if (!label)
        return;
    for (const child of parent.children) {
        const base = typeof child.aiSlot === 'string' ? child.aiSlot.split(':')[0].replace(/\.\d+$/, '') : '';
        if (child.type !== 'text' || (base !== 'eyebrow' && base !== 'slot-eyebrow' && base !== 'label'))
            continue;
        child.content = label;
        delete child.aiSlot;
    }
}
/**
 * Marca y rellena las tarjetas de contacto (worker). Sólo se llama sobre una
 * sección de contacto; las que no tienen dato se quedan, con su marca, para que
 * el asistente decida al componer.
 */
export function markContactCards(nodes, entries, options = {}) {
    const visit = (node) => {
        const parts = cardParts(node);
        if (parts) {
            fill(parts.action, parts.slot, entries, options);
            return;
        }
        node.children.forEach(visit);
    };
    nodes.forEach(visit);
}
/**
 * Rellena las tarjetas YA marcadas con los datos de ahora (asistente). Las que
 * siguen sin dato quedan «por completar» (ver arriba); las no marcadas no se
 * tocan.
 */
export function fillContactCards(nodes, entries, options = {}) {
    const visit = (node) => {
        /* Un texto o un enlace marcado: se rellena él mismo. Antes sólo un texto
           suelto; un enlace marcado sólo se encontraba si era el PRIMER enlace de
           su tarjeta, y con un icono enlazado delante el teléfono se quedaba en
           «Agrega tu teléfono» aunque ya estuviera escrito. */
        if (TEXT_TYPES.has(node.type) && typeof node.contactSlot === 'string' && node.contactSlot in KINDS_BY_SLOT) {
            const copy = { ...node };
            fill(copy, node.contactSlot, entries, options);
            return copy;
        }
        /* Un contenedor con varios marcados (teléfono y correo, lado a lado): cada
           uno se rellena él mismo al recorrer los hijos. Antes aquí se rellenaba
           sólo el PRIMER enlace marcado y se paraba: el correo se quedaba en
           «Agrega tu correo» aunque se hubiera dado. */
        const children = node.children.map(visit);
        return children.every((child, index) => child === node.children[index]) ? node : { ...node, children };
    };
    return nodes.map(visit);
}
/** El dato que se pide para cada hueco: el primero que lo llena. */
const KIND_FOR_SLOT = {
    phone: 'phone', email: 'email', address: 'address', web: 'website',
    instagram: 'instagram', facebook: 'facebook', linkedin: 'linkedin', youtube: 'youtube',
};
/**
 * Los datos de contacto que la página enseña (sus huecos marcados,
 * `contactSlot`) y que todavía no se han dado: sin ellos salen «Agrega tu
 * dirección». En el orden en que aparecen, sin repetir. El de mensaje no
 * cuenta: sin dato lleva al formulario.
 */
export function missingContactKinds(nodes, entries) {
    const given = new Set(entries.filter((entry) => entry.value.trim()).map((entry) => entry.kind));
    const found = [];
    const visit = (node) => {
        /* Un mapa sin dirección propia toma la del negocio: sin ella, sale de muestra. */
        const slot = typeof node.contactSlot === 'string' ? node.contactSlot : isMapWithoutAddress(node) ? 'address' : null;
        if (slot && slot in KINDS_BY_SLOT && !KINDS_BY_SLOT[slot].some((kind) => given.has(kind))) {
            const kind = KIND_FOR_SLOT[slot];
            if (kind && !found.includes(kind))
                found.push(kind);
        }
        node.children.forEach(visit);
    };
    nodes.forEach(visit);
    return found;
}
/** Un mapa («Mapa», un embed con sus ajustes `zoom=…`) que todavía no tiene dirección (`q=`). */
export function isMapWithoutAddress(node) {
    if (node.type !== 'embed' || typeof node.content !== 'string')
        return false;
    /* A mano y no con `URLSearchParams`: este paquete compila sin las librerías
       del navegador (rompió el deploy del worker). */
    const params = new Map(node.content.trim().split('&').map((pair) => {
        const at = pair.indexOf('=');
        return at < 0 ? [pair, ''] : [pair.slice(0, at), pair.slice(at + 1)];
    }));
    return params.has('zoom') && !(params.get('q') ?? '').replace(/\+/g, ' ').trim();
}
/** Si alguno de estos nodos es un mapa sin dirección. */
export function hasMapWithoutAddress(nodes) {
    const visit = (node) => isMapWithoutAddress(node) || node.children.some(visit);
    return nodes.some(visit);
}
//# sourceMappingURL=contactCards.js.map